// StayMii Beds24 Knowledge Centre — content registry.
// Batch A: Core Foundation (10 pages). Add API / e-Invoice / Tutorial / Comparison
// batches by appending objects to `beds24Articles` and entries to SITEMAP_ROUTES.

export const CATEGORY_ORDER = ['Core', 'API', 'e-Invoice', 'Tutorial', 'Comparison'];

export const CATEGORY_META = {
  Core: {
    title: 'Foundation & Basics',
    blurb: 'Understand what Beds24 is, how it works, and whether it fits your property.',
    badge: 'bg-blue-500/10 text-blue-300 border-blue-500/20',
    tile: 'bg-blue-50 text-blue-600',
  },
  API: {
    title: 'API & Developer Guides',
    blurb: 'Authentication, endpoints, webhooks and best practices for the Beds24 API.',
    badge: 'bg-purple-500/10 text-purple-300 border-purple-500/20',
    tile: 'bg-purple-50 text-purple-600',
  },
  'e-Invoice': {
    title: 'Malaysian e-Invoice',
    blurb: 'Connect Beds24 to LHDN MyInvois and automate compliant hotel invoicing.',
    badge: 'bg-emerald-500/10 text-emerald-300 border-emerald-500/20',
    tile: 'bg-emerald-50 text-emerald-600',
  },
  Tutorial: {
    title: 'Tutorials & Setup',
    blurb: 'Step-by-step walkthroughs for connecting channels, payments and automation.',
    badge: 'bg-amber-500/10 text-amber-300 border-amber-500/20',
    tile: 'bg-amber-50 text-amber-600',
  },
  Comparison: {
    title: 'Comparisons & Buying Guides',
    blurb: 'How Beds24 stacks up against other PMS and channel managers.',
    badge: 'bg-rose-500/10 text-rose-300 border-rose-500/20',
    tile: 'bg-rose-50 text-rose-600',
  },
};

export const formatBeds24Date = (iso) => {
  if (!iso) return '';
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const [y, m, d] = iso.split('-');
  return `${parseInt(d, 10)} ${months[parseInt(m, 10) - 1]} ${y}`;
};

const beds24Articles = [
  /* ============================ 1 ============================ */
  {
    slug: 'what-is-beds24',
    category: 'Core',
    seoTitle: 'What is Beds24? A Plain-English Guide (2026)',
    title: 'What is Beds24? A Plain-English Guide for Property Owners',
    description: 'Beds24 is a cloud-based property management system and channel manager. Learn exactly what it does, who it is for, how it works, and where it fits in your tech stack.',
    keywords: 'what is beds24, beds24 explained, beds24 pms, beds24 channel manager, beds24 meaning',
    updated: '2026-07-17',
    published: '2026-07-17',
    readTime: '9 min read',
    sections: [
      {
        heading: 'What is Beds24, in one sentence?',
        body: `Beds24 is a **cloud-based property management system (PMS) and channel manager** that lets accommodation providers manage bookings, availability, rates and guest communication from a single online dashboard, while automatically keeping dozens of booking channels in sync.

If you rent out rooms — a hotel, hostel, guesthouse, serviced apartment, homestay or short-term rental — Beds24 sits at the centre of your operation. It receives reservations from every channel you sell on (Booking.com, Airbnb, Agoda, Expedia and your own website), updates availability everywhere the moment a room is sold, and gives you one calendar to run everything from.

::info Beds24 has been operating since 2006 and is known in the industry for being highly flexible, developer-friendly and competitively priced compared with enterprise hospitality software.`,
      },
      {
        heading: 'PMS vs channel manager vs booking engine',
        body: `These three terms are often confused. Beds24 actually bundles all three, which is why it can replace several separate tools.

| Component | What it does | Beds24 role |
|---|---|---|
| **PMS** | Stores bookings, guests, rooms, rates; your operational calendar | Core dashboard & calendar |
| **Channel Manager** | Syncs availability & rates to OTAs; imports their bookings | 100+ channel connections |
| **Booking Engine** | Takes commission-free direct bookings on your own site | Built-in, customisable widget |

Without a channel manager, selling the same room on Booking.com and Airbnb risks **double bookings** — two guests booking the last room at the same time. Beds24 prevents this by instantly closing availability across every channel when a booking comes in.`,
      },
      {
        heading: 'Who uses Beds24?',
        body: `Beds24 is popular with independent operators who want power and control without enterprise pricing:

- **Independent hotels & boutique properties** managing 5–100 rooms
- **Hostels** needing dorm-bed and shared-room logic
- **Serviced apartments & aparthotels** with multi-unit inventory
- **Vacation & short-term rentals** listing on Airbnb and Vrbo
- **Homestays and guesthouses** in Malaysia and across Asia
- **Property managers** running many owners' units from one account

Its flexibility is its signature strength — almost every rule, rate plan, email and report can be customised. That same flexibility means it has a steeper learning curve than simpler tools, which is where guides like this one (and partners like StayMii) help.`,
      },
      {
        heading: 'How Beds24 works day to day',
        body: `A typical flow looks like this:

1. You set up your **rooms/units**, **rate plans** and **availability** once inside Beds24.
2. You connect your **channels** (Booking.com, Airbnb, etc.) so they read from Beds24.
3. A guest books on any channel. Beds24 **imports the reservation** and **closes that room** everywhere else within minutes.
4. Automated **confirmation and pre-arrival emails** go out to the guest.
5. You see every arrival, departure and payment on one **calendar and dashboard**.
6. At checkout you (or an integration) generate the **invoice** and collect any balance.

Everything above can be automated with Beds24's rules engine, webhooks and API — which is what turns it from a booking calendar into an operations platform.`,
      },
      {
        heading: 'Key features at a glance',
        body: `- **Channel management** to 100+ OTAs with two-way sync
- **Central calendar** with drag-and-drop bookings and colour coding
- **Commission-free booking engine** for your own website
- **Automated guest messaging** (email/SMS templates with triggers)
- **Flexible rate management** — derived rates, seasonal pricing, length-of-stay rules
- **Payment collection** via Stripe and other gateways
- **Powerful API (v1 & v2) and webhooks** for custom integrations
- **Reports** for occupancy, revenue and channel performance

For a deeper breakdown see [Beds24 Features Explained](/beds24/beds24-features-explained).`,
      },
      {
        heading: 'What Beds24 does NOT do out of the box',
        body: `Beds24 is deliberately a horizontal platform for the whole world, so some country-specific needs are not built in:

::warn Beds24 does **not** natively produce **LHDN-compliant Malaysian e-invoices**. It can email a booking confirmation or a simple invoice, but it cannot submit a validated e-invoice to Malaysia's **MyInvois** system.

This matters because, since July 2025, every Malaysian business must issue e-invoices for all transactions. That is exactly the gap [StayMii's Beds24 e-Invoice integration](/beds24/staymii-beds24-integration) fills — it reads bookings from Beds24 and turns them into compliant, auto-submitted e-invoices.`,
      },
      {
        heading: 'Is Beds24 right for you?',
        body: `Beds24 is an excellent fit if you want maximum flexibility, a strong API, and channel management at a fair price — and you're comfortable investing a little time to learn it (or working with a partner).

If you're just getting started, read the [Beds24 Complete Guide](/beds24/beds24-complete-guide) next, or if you operate in Malaysia jump to [Beds24 Malaysia](/beds24/beds24-malaysia) for local specifics like SST, tourism tax and e-Invoice.`,
      },
    ],
    faqs: [
      { q: 'Is Beds24 free?', a: 'Beds24 is a paid product but is competitively priced, typically billed per property/unit per month. It often runs a free trial. See the [Beds24 Pricing Guide](/beds24/beds24-pricing-guide) for the current model.' },
      { q: 'Is Beds24 a PMS or a channel manager?', a: 'Both. Beds24 combines a property management system, a channel manager and a booking engine in one platform.' },
      { q: 'Does Beds24 work for Airbnb?', a: 'Yes. Beds24 connects to Airbnb (and Booking.com, Agoda, Expedia, Vrbo and more) with two-way availability and rate sync.' },
      { q: 'Can Beds24 issue Malaysian e-invoices?', a: 'Not natively. Beds24 does not submit to LHDN MyInvois. StayMii adds this by reading Beds24 bookings and generating compliant, auto-submitted e-invoices. See [StayMii + Beds24](/beds24/staymii-beds24-integration).' },
    ],
    related: ['beds24-complete-guide', 'beds24-features-explained', 'beds24-malaysia', 'why-hotels-use-beds24', 'staymii-beds24-integration'],
  },

  /* ============================ 2 ============================ */
  {
    slug: 'beds24-complete-guide',
    category: 'Core',
    seoTitle: 'Beds24 Complete Guide (2026): Setup, Features & Tips',
    title: 'The Beds24 Complete Guide: Everything You Need to Get Started',
    description: 'A comprehensive, step-by-step Beds24 guide covering account setup, rooms and rates, channel connections, the booking engine, automation, and going live — plus Malaysian e-Invoice.',
    keywords: 'beds24 guide, beds24 tutorial, beds24 setup, beds24 how to, beds24 getting started',
    updated: '2026-07-17',
    published: '2026-07-17',
    readTime: '12 min read',
    sections: [
      {
        heading: 'Who this guide is for',
        body: `This guide walks a brand-new Beds24 user from an empty account to a fully live property syncing with OTAs. It's written for owners and managers, not developers — though we link to the [Beds24 API guides](/beds24/staymii-beds24-integration) where relevant.

If you have never used Beds24 before, start with [What is Beds24?](/beds24/what-is-beds24) for the concepts, then come back here for the hands-on steps.`,
      },
      {
        heading: 'Step 1 — Create your account and property',
        body: `1. Sign up at **beds24.com** and start the free trial.
2. Create your first **property** — this represents one physical location (a hotel, a building, a house).
3. Set the property **time zone, currency and address**. In Malaysia, set currency to **MYR** and time zone to **Asia/Kuala_Lumpur**.
4. Configure your **check-in / check-out times** and default policies.

::tip Get the currency and time zone right before importing any bookings — changing them later can misalign your calendar and rates.`,
      },
      {
        heading: 'Step 2 — Add rooms, units and rate plans',
        body: `Beds24 separates **room types** (a category you sell, e.g. "Deluxe Queen") from **units** (the individual physical rooms within that type).

- Create each **room type** with its capacity, default price and number of units.
- Add **rate plans** — for example a standard rate, a non-refundable rate, and a breakfast-included rate.
- Use **derived rates** so a discount rate automatically tracks your base rate (e.g. non-refundable = base − 10%).
- Set **seasonal pricing** and **length-of-stay** rules where needed.

Rate flexibility is one of Beds24's biggest strengths; see [Beds24 Features Explained](/beds24/beds24-features-explained) for the full rate toolkit.`,
      },
      {
        heading: 'Step 3 — Connect your channels',
        body: `This is where the channel manager earns its keep. For each OTA:

1. In Beds24, open **Settings → Channel Manager** and pick the channel (Booking.com, Airbnb, Agoda, Expedia, Vrbo…).
2. Follow the channel-specific connection flow (usually an API key, XML connection request, or an authorisation login).
3. **Map** each Beds24 room type to the matching room on the OTA.
4. Push availability and rates, then run a test to confirm two-way sync.

::warn Incorrect room mapping is the #1 cause of channel errors. Double-check that each OTA room points to the correct Beds24 room type before going live.`,
      },
      {
        heading: 'Step 4 — Set up your booking engine',
        body: `Beds24 includes a **commission-free booking engine** you embed on your own website. Direct bookings avoid OTA commission (typically 15–18%), so every direct reservation is more profitable.

- Customise the booking widget's colours and fields to match your brand.
- Connect a **payment gateway** (Stripe is the most common) to take deposits or full payment.
- Add the booking engine to your site via an embed code or a hosted page link.

A healthy direct-booking share is one of the fastest ways to improve margins — see [Why Hotels Use Beds24](/beds24/why-hotels-use-beds24).`,
      },
      {
        heading: 'Step 5 — Automate guest messaging',
        body: `Set up automated email (and optionally SMS) templates triggered by booking events:

- **Booking confirmation** on new reservation
- **Pre-arrival** email with directions, Wi-Fi and check-in instructions
- **Pre-departure** and **post-checkout** follow-ups (great for review requests)

Templates support variables like guest name, dates and balance due, so each message is personalised automatically. This removes hours of manual work every week.`,
      },
      {
        heading: 'Step 6 — Payments, invoices and going live',
        body: `Before you flip the switch:

- Confirm your **payment gateway** is live and test a small charge.
- Decide how you'll handle **invoices**. Beds24 can produce a basic invoice, but Malaysian operators need **LHDN-compliant e-invoices** — connect [StayMii](/beds24/staymii-beds24-integration) for that.
- Do a **final availability push** to all channels and verify the calendar matches each OTA.
- Take a **test booking** through your own booking engine end-to-end.

Once everything reconciles, you're live.`,
      },
      {
        heading: 'Step 7 — Malaysian compliance (important)',
        body: `If you operate in Malaysia, going live is not the finish line — tax compliance is.

::info Every booking must become an **LHDN e-invoice** submitted to **MyInvois**, with correct **SST (8%)**, **service charge** and **tourism tax (RM10/room/night for foreign guests)**.

Beds24 won't do this alone. [StayMii's Beds24 e-Invoice integration](/beds24/staymii-beds24-integration) reads every Beds24 booking and produces a compliant, auto-submitted e-invoice — no manual re-keying. See [Beds24 Malaysia](/beds24/beds24-malaysia) for the local playbook.`,
      },
      {
        heading: 'Common beginner mistakes to avoid',
        body: `- Setting the **wrong currency/time zone** and misaligning the calendar
- **Mis-mapping** OTA rooms to Beds24 room types (causes overbookings)
- Forgetting to enable **derived rates**, then editing every rate by hand
- Not testing the **booking engine** payment flow before launch
- Assuming Beds24 handles **Malaysian e-invoicing** — it doesn't

Work through the steps in order and you'll avoid nearly all of them.`,
      },
    ],
    faqs: [
      { q: 'How long does Beds24 setup take?', a: 'A single small property with two or three channels can be live in a day. Larger properties with many room types and channels take longer, mostly due to channel mapping and testing.' },
      { q: 'Do I need a developer to use Beds24?', a: 'No. The core setup is done through the web dashboard. Developers only come in for custom API integrations — and even then, partners like StayMii handle common ones such as e-Invoice.' },
      { q: 'Can I import my existing bookings?', a: 'Yes. When you connect a channel, existing future reservations are typically imported. You can also import via calendar sync or the API.' },
      { q: 'What is the first thing to set up?', a: 'Your property currency and time zone, then room types and rate plans, then channels. Getting the order right prevents calendar and pricing errors later.' },
    ],
    related: ['what-is-beds24', 'beds24-features-explained', 'beds24-pricing-guide', 'beds24-malaysia', 'staymii-beds24-integration'],
  },

  /* ============================ 3 ============================ */
  {
    slug: 'beds24-malaysia',
    category: 'Core',
    seoTitle: 'Beds24 Malaysia: Setup, SST, Tourism Tax & e-Invoice',
    title: 'Beds24 in Malaysia: The Local Operator’s Playbook',
    description: 'How Malaysian hotels and short-term rentals should configure Beds24 — currency, SST, service charge, tourism tax, OTA mix, and mandatory LHDN e-Invoice compliance with StayMii.',
    keywords: 'beds24 malaysia, beds24 sst, beds24 tourism tax, beds24 lhdn, beds24 e-invoice malaysia, beds24 hotel malaysia',
    updated: '2026-07-17',
    published: '2026-07-17',
    readTime: '11 min read',
    sections: [
      {
        heading: 'Why Malaysian operators love Beds24',
        body: `Beds24 is widely used across Malaysia — from Kuala Lumpur serviced apartments to Penang guesthouses and Langkawi resorts — because it connects to the OTAs that matter here (Booking.com, Agoda, Airbnb, Expedia, Traveloka via partners) at a price independent operators can afford.

But Beds24 is a global product. To run it correctly in Malaysia you need to configure it for local currency, taxes and — critically — **LHDN e-Invoice compliance**. This guide covers each.`,
      },
      {
        heading: 'Base configuration for Malaysia',
        body: `Set these before anything else:

- **Currency:** MYR (Malaysian Ringgit)
- **Time zone:** Asia/Kuala_Lumpur
- **Language:** English (and add others your guests use)
- **Check-in/out:** typically 15:00 / 12:00

Getting these right first prevents rate and calendar misalignment when OTA bookings start flowing in.`,
      },
      {
        heading: 'Malaysian hotel taxes you must handle',
        body: `Three charges commonly apply to Malaysian accommodation:

| Charge | Rate | Applies to |
|---|---|---|
| **Service Tax (SST)** | 8% | Accommodation & services (if annual revenue > RM500,000) |
| **Service Charge** | commonly 10% | Hotel services, where applied |
| **Tourism Tax** | RM10 / room / night | **Foreign (non-Malaysian) guests only** |

::warn Tourism tax is **per room per night**, not per guest. A family of four in one room for three nights owes RM30 — not RM120. It also applies **only to foreign guests**; Malaysians and permanent residents are exempt.

Beds24 can add these as extra charges, but it will not calculate and submit them in a **compliant e-invoice** — that's the compliance gap.`,
      },
      {
        heading: 'The LHDN e-Invoice mandate (the big one)',
        body: `Since **July 2025 (Phase 3)**, **every** Malaysian business — regardless of revenue — must issue **e-invoices** through LHDN's **MyInvois** platform. That includes every hotel, homestay and Airbnb host.

::info An e-invoice is not a PDF. It is a structured document validated by LHDN in near real time, which returns a unique **UUID** and a **QR code**. Non-compliance carries fines up to **RM50,000** or imprisonment up to **3 years** per offence.

Beds24 does not connect to MyInvois. So a Beds24-only setup in Malaysia is **not compliant** on its own.`,
      },
      {
        heading: 'How StayMii closes the compliance gap',
        body: `[StayMii](/beds24/staymii-beds24-integration) is built specifically for Malaysian hospitality compliance. It:

- Reads every booking from Beds24 in real time (all channels)
- Calculates **SST, service charge and tourism tax** correctly per booking
- Generates an **LHDN-compliant e-invoice** and **auto-submits** it to MyInvois
- Stores the validation **UUID and QR** against the original Beds24 booking
- Issues **Credit/Debit Notes** automatically for cancellations and rate changes

Setup takes under 15 minutes and requires no migration off Beds24. You keep Beds24 for operations; StayMii handles compliance.`,
      },
      {
        heading: 'Handling OTA bookings for e-Invoice',
        body: `Most Malaysian properties get the majority of bookings from OTAs, which complicates invoicing:

- **Booking.com / Agoda pay-at-hotel:** the hotel collects payment and issues the e-invoice to the guest.
- **Agoda / Expedia prepaid (OTA-collect):** the OTA collects; the hotel still issues the guest e-invoice for the amount the guest paid, and records the commission as an expense.
- **Airbnb:** the host always invoices the guest; Airbnb's host fee is a separate expense.
- **Missing guest TIN:** for guests without a Malaysian TIN, LHDN's general public TIN can be used.

StayMii applies the correct rule automatically based on the booking's channel — see [StayMii + Beds24](/beds24/staymii-beds24-integration).`,
      },
      {
        heading: 'A compliant Malaysian Beds24 stack',
        body: `Putting it together, a correctly configured Malaysian operator runs:

1. **Beds24** — bookings, channels, calendar, guest messaging (MYR, KL time zone)
2. **Payment gateway** (e.g. Stripe) — deposits and balances
3. **StayMii** — SST/tourism-tax-correct **e-invoices auto-submitted to MyInvois**

This gives you global-grade channel management **and** local compliance. Start with the [Beds24 Complete Guide](/beds24/beds24-complete-guide), then connect StayMii.`,
      },
    ],
    faqs: [
      { q: 'Is Beds24 legal to use in Malaysia?', a: 'Yes — Beds24 is perfectly fine to use. The compliance requirement is on invoicing: you must issue LHDN e-invoices, which Beds24 alone does not do. Pairing Beds24 with StayMii makes the setup compliant.' },
      { q: 'Does Beds24 calculate SST and tourism tax?', a: 'Beds24 can add them as extra charges if you configure them, but it will not produce a compliant LHDN e-invoice with the correct breakdown. StayMii handles the calculation and submission.' },
      { q: 'Do Airbnb hosts in Malaysia need e-invoices?', a: 'Yes. Since July 2025, all businesses including individual short-term rental hosts must issue e-invoices, regardless of revenue.' },
      { q: 'Can I keep Beds24 and still be compliant?', a: 'Absolutely. Keep Beds24 for operations and add StayMii for e-Invoice. No migration is required and setup takes under 15 minutes.' },
    ],
    related: ['what-is-beds24', 'beds24-complete-guide', 'staymii-beds24-integration', 'why-hotels-use-beds24', 'beds24-faq'],
  },

  /* ============================ 4 ============================ */
  {
    slug: 'why-hotels-use-beds24',
    category: 'Core',
    seoTitle: 'Why Hotels Use Beds24: 8 Real Reasons (2026)',
    title: 'Why Hotels Use Beds24: The Real Reasons Operators Choose It',
    description: 'From double-booking prevention and commission-free direct bookings to a powerful API and fair pricing — here are the concrete reasons independent hotels choose Beds24.',
    keywords: 'why use beds24, beds24 benefits, beds24 advantages, beds24 for hotels, beds24 pros',
    updated: '2026-07-17',
    published: '2026-07-17',
    readTime: '9 min read',
    sections: [
      {
        heading: 'The core problem Beds24 solves',
        body: `Every property that sells rooms on more than one channel faces the same nightmare: **double bookings**. Sell the last room on Booking.com and Airbnb at the same moment and you've oversold — an expensive, reputation-damaging problem.

Beds24's channel manager solves this by holding a **single source of truth** for availability and instantly closing a room across every channel when it sells. That one capability alone justifies the tool for most multi-channel operators.`,
      },
      {
        heading: '1. Prevents double bookings',
        body: `Two-way sync means a booking on any channel updates availability everywhere within minutes. No more manually blocking dates across five extranets. For busy properties this eliminates the single biggest operational risk of selling online.`,
      },
      {
        heading: '2. Commission-free direct bookings',
        body: `Beds24's built-in **booking engine** lets guests book on your own website with no OTA commission. If OTAs take 15–18% and you shift even a fifth of bookings to direct, the margin improvement is substantial — often paying for the software many times over.`,
      },
      {
        heading: '3. Reaches 100+ channels',
        body: `Beds24 connects to the channels that fill rooms — Booking.com, Airbnb, Agoda, Expedia, Vrbo and many regional players. One dashboard distributes your inventory everywhere your guests search.`,
      },
      {
        heading: '4. Deep flexibility and automation',
        body: `Beds24 is famous for letting you customise almost everything: derived and seasonal rates, length-of-stay rules, automated guest emails, and an **automation/rules engine** that can trigger actions on booking events. Power users automate huge chunks of daily operations.`,
      },
      {
        heading: '5. A genuinely powerful API',
        body: `Unlike many closed PMS platforms, Beds24 exposes a comprehensive **API (v1 and v2) plus webhooks**. This is why an ecosystem of integrations exists around it — including [StayMii's e-Invoice integration](/beds24/staymii-beds24-integration). If you can imagine an automation, the API usually makes it possible.`,
      },
      {
        heading: '6. Fair, scalable pricing',
        body: `Beds24 is priced for independents — typically per property/unit — rather than the steep per-room enterprise pricing of some competitors. Small operators can afford proper channel management without an enterprise budget. See the [Beds24 Pricing Guide](/beds24/beds24-pricing-guide).`,
      },
      {
        heading: '7. Handles complex inventory',
        body: `Dorm beds, multi-unit apartments, shared rooms, and mixed-use properties are all supported. This makes Beds24 a favourite for hostels and serviced-apartment operators whose inventory doesn't fit a simple "hotel room" model.`,
      },
      {
        heading: '8. Works with a compliance layer for Malaysia',
        body: `For Malaysian operators, Beds24 plus [StayMii](/beds24/staymii-beds24-integration) delivers global channel management **and** local **LHDN e-Invoice** compliance. You get the best of both without leaving Beds24.

::tip The operators happiest with Beds24 treat it as a flexible platform and pair it with focused partners (payments, e-Invoice, analytics) rather than expecting one tool to do everything perfectly.`,
      },
      {
        heading: 'The honest trade-off',
        body: `Beds24's flexibility comes with a **learning curve**. The interface is dense and setup rewards patience. That's the main reason some operators hesitate. The fix is good guidance — this Knowledge Centre, Beds24's own docs, and partners who handle the tricky parts like [e-Invoice](/beds24/staymii-beds24-integration). If you want power and value and will invest a little time, Beds24 is hard to beat. For a balanced view, read the [Beds24 Review](/beds24/beds24-review).`,
      },
    ],
    faqs: [
      { q: 'Is Beds24 good for small properties?', a: 'Yes. Its per-unit pricing and flexibility make it a strong choice for small independents, homestays and hostels that still want full channel management.' },
      { q: 'Does Beds24 reduce OTA commissions?', a: 'Indirectly — its commission-free booking engine lets you win direct bookings, shifting revenue away from commission-charging OTAs.' },
      { q: 'What is the main downside of Beds24?', a: 'The learning curve. Its power and flexibility make the interface dense. Guides and integration partners significantly reduce the pain.' },
      { q: 'Why pair Beds24 with StayMii in Malaysia?', a: 'Beds24 handles bookings and channels; StayMii adds mandatory LHDN e-Invoice automation with correct SST and tourism tax. Together they form a compliant stack.' },
    ],
    related: ['what-is-beds24', 'beds24-features-explained', 'beds24-review', 'beds24-pricing-guide', 'staymii-beds24-integration'],
  },

  /* ============================ 5 ============================ */
  {
    slug: 'beds24-features-explained',
    category: 'Core',
    seoTitle: 'Beds24 Features Explained: Full Breakdown (2026)',
    title: 'Beds24 Features Explained: A Complete Breakdown',
    description: 'A feature-by-feature tour of Beds24 — channel manager, calendar, booking engine, rate management, automation, payments, reports and the API — and what each one means for your property.',
    keywords: 'beds24 features, beds24 booking engine, beds24 automation, beds24 rate management, beds24 reports',
    updated: '2026-07-17',
    published: '2026-07-17',
    readTime: '11 min read',
    sections: [
      {
        heading: 'How to read this feature list',
        body: `Beds24 has a lot of features — that's part of its appeal and part of its complexity. We've grouped them by what they do for your business so you can quickly see what matters to you. For the basics first, read [What is Beds24?](/beds24/what-is-beds24).`,
      },
      {
        heading: 'Channel manager',
        body: `The heart of Beds24. Two-way integration with 100+ OTAs keeps availability and rates in sync and imports reservations automatically.

- Real-time availability updates to prevent overbooking
- Rate and restriction push (min-stay, closed-to-arrival, etc.)
- Automatic booking import with guest details
- Per-channel mark-ups or mark-downs

This is what turns "listing on many sites" from a liability into a scalable strategy.`,
      },
      {
        heading: 'Central calendar & bookings',
        body: `A single, colour-coded calendar shows every arrival, stay and departure. You can drag bookings between units, block dates, and see availability at a glance. Booking records hold guest details, payments, notes and communication history — your operational nerve centre.`,
      },
      {
        heading: 'Commission-free booking engine',
        body: `An embeddable widget that takes direct bookings on your own website with **zero commission**. Fully customisable fields and styling, multi-currency, and connected to your payment gateway. Every direct booking skips OTA commission — the single highest-leverage revenue feature Beds24 offers.`,
      },
      {
        heading: 'Rate management',
        body: `Beds24's pricing engine is unusually powerful:

- **Derived rates** — child rates that auto-track a base rate (e.g. −10%)
- **Seasonal & date-range pricing**
- **Length-of-stay** and **occupancy-based** pricing
- **Yield/auto-pricing** rules to nudge rates with demand

::tip Set up derived rates early. They save enormous manual effort — change one base rate and every dependent rate updates automatically.`,
      },
      {
        heading: 'Automation & guest messaging',
        body: `Trigger-based **email and SMS** templates handle confirmations, pre-arrival instructions, upsells and post-stay review requests. The broader **automation/rules engine** can act on booking events — assigning units, sending notifications, or updating statuses — reducing repetitive manual work to near zero.`,
      },
      {
        heading: 'Payments',
        body: `Connect gateways such as **Stripe** to collect deposits, full prepayment or balances. You can store cards (where permitted), charge no-show fees, and reconcile payments against bookings. For a walkthrough see the upcoming [Connect Stripe](/beds24/staymii-beds24-integration) tutorial in the Knowledge Centre.`,
      },
      {
        heading: 'Reports & analytics',
        body: `Built-in reports cover occupancy, revenue, channel performance, arrivals/departures and more. These help you spot your best channels, track pace, and make pricing decisions. For Malaysian tax reporting (SST, tourism tax, e-Invoice status) you'll want [StayMii](/beds24/staymii-beds24-integration) layered on top.`,
      },
      {
        heading: 'API & webhooks',
        body: `Beds24 exposes a full **REST API (v1 and v2)** and **webhooks** for real-time events. This developer-friendliness is why so many integrations exist — analytics tools, revenue managers, and compliance layers like StayMii's e-Invoice engine all plug in here.

\`\`\`http
GET https://api.beds24.com/v2/bookings
Authorization: token YOUR_LONG_LIFE_TOKEN
\`\`\`

We cover authentication and endpoints in depth in the API & Developer Guides section.`,
      },
      {
        heading: 'What is missing (and how to add it)',
        body: `Two gaps matter most for Malaysian operators:

::warn Beds24 has **no native LHDN e-Invoice** submission and **no Malaysia-specific tax reporting**.

Both are solved by adding [StayMii](/beds24/staymii-beds24-integration), which reads Beds24 bookings and produces compliant, auto-submitted e-invoices with correct SST and tourism tax. See [Beds24 Malaysia](/beds24/beds24-malaysia).`,
      },
    ],
    faqs: [
      { q: 'What is the most valuable Beds24 feature?', a: 'For most operators it is the combination of the channel manager (prevents overbooking) and the commission-free booking engine (grows direct revenue).' },
      { q: 'Does Beds24 have automation?', a: 'Yes. Beds24 offers trigger-based guest messaging and a broader automation/rules engine that can act on booking events.' },
      { q: 'Can Beds24 connect to Stripe?', a: 'Yes. Beds24 integrates with Stripe and other gateways to collect deposits, prepayments and balances.' },
      { q: 'Does Beds24 report SST and tourism tax?', a: 'Not in a Malaysia-compliant form. Use StayMii for SST, tourism tax and e-Invoice reporting on top of Beds24.' },
    ],
    related: ['what-is-beds24', 'beds24-complete-guide', 'why-hotels-use-beds24', 'beds24-pricing-guide', 'staymii-beds24-integration'],
  },

  /* ============================ 6 ============================ */
  {
    slug: 'beds24-pricing-guide',
    category: 'Core',
    seoTitle: 'Beds24 Pricing Guide (2026): How the Cost Works',
    title: 'Beds24 Pricing Guide: How Much Does Beds24 Cost?',
    description: 'Understand the Beds24 pricing model — how it is calculated, what affects your monthly cost, how it compares to competitors, and the true total cost of a compliant Malaysian setup.',
    keywords: 'beds24 pricing, beds24 cost, beds24 price, how much is beds24, beds24 subscription',
    updated: '2026-07-17',
    published: '2026-07-17',
    readTime: '8 min read',
    sections: [
      {
        heading: 'How Beds24 pricing works',
        body: `Beds24 uses a **usage-based subscription** billed primarily on the **number of properties and units (rooms)** you manage, rather than a steep per-room enterprise fee. This is a big reason independents choose it: a small property pays a small amount.

::info Because pricing and promotions change, always confirm current figures on **beds24.com**. This guide explains the *model* and the *drivers* so you can budget accurately, rather than quoting a price that may be out of date.`,
      },
      {
        heading: 'What drives your monthly cost',
        body: `Your Beds24 bill is influenced by:

- **Number of units/rooms** — the primary driver
- **Number of properties** — multi-property accounts scale up
- **Add-on modules** — certain advanced features or higher API limits
- **Billing cycle** — annual billing is usually cheaper than monthly

For most small independents, Beds24 lands well below enterprise PMS pricing, which is central to its value proposition (see [Why Hotels Use Beds24](/beds24/why-hotels-use-beds24)).`,
      },
      {
        heading: 'Free trial',
        body: `Beds24 typically offers a **free trial** so you can build your setup and test channel connections before paying. Use it to complete the [Beds24 Complete Guide](/beds24/beds24-complete-guide) steps and take a test booking end-to-end before committing.`,
      },
      {
        heading: 'The total cost of a real setup',
        body: `Software subscription is only part of the picture. Budget for the whole stack:

| Cost item | Typical driver |
|---|---|
| **Beds24 subscription** | Units/properties |
| **Payment gateway fees** | Per transaction (e.g. Stripe %) |
| **OTA commissions** | 15–18% of OTA bookings |
| **e-Invoice / compliance** | Malaysia: [StayMii](/beds24/staymii-beds24-integration) |
| **Setup time** | Your team's hours (or a partner) |

::tip The biggest lever on total cost is **OTA commission**. Growing direct bookings via the commission-free booking engine usually saves far more than the software costs.`,
      },
      {
        heading: 'Beds24 vs competitor pricing',
        body: `Compared with Cloudbeds, Little Hotelier and eZee, Beds24 is generally positioned as the **value/flexibility** option — lower entry cost, more configurability, but a steeper learning curve. Enterprise tools bundle more hand-holding and polish at a higher price. Detailed head-to-heads live in the Comparisons section of this Knowledge Centre.`,
      },
      {
        heading: 'Is Beds24 worth it?',
        body: `For independents who value flexibility and fair pricing, Beds24 offers excellent return: it prevents costly overbookings, grows commission-free direct revenue, and automates hours of manual work. The main "cost" is the time to learn it.

For Malaysian operators, factor in [StayMii](/beds24/staymii-beds24-integration) for e-Invoice compliance — a small addition that avoids fines up to **RM50,000** per non-compliance offence. See [Beds24 Malaysia](/beds24/beds24-malaysia).`,
      },
    ],
    faqs: [
      { q: 'How much does Beds24 cost per month?', a: 'It depends mainly on your number of units/properties and billing cycle. Beds24 is positioned as affordable for independents; check beds24.com for the current figures as they change.' },
      { q: 'Does Beds24 charge commission on bookings?', a: 'No. Beds24 charges a subscription, not commission. Its booking engine is commission-free, though OTAs still charge their own commission on OTA bookings.' },
      { q: 'Is there a Beds24 free trial?', a: 'Yes, Beds24 typically offers a free trial so you can set up and test before paying.' },
      { q: 'What extra costs should Malaysian operators budget for?', a: 'Payment gateway fees, OTA commissions, and an e-Invoice compliance layer such as StayMii to submit LHDN-compliant invoices.' },
    ],
    related: ['what-is-beds24', 'beds24-complete-guide', 'why-hotels-use-beds24', 'beds24-review', 'staymii-beds24-integration'],
  },

  /* ============================ 7 ============================ */
  {
    slug: 'beds24-review',
    category: 'Core',
    seoTitle: 'Beds24 Review (2026): Pros, Cons & Who It’s For',
    title: 'Beds24 Review: An Honest Look at the Pros and Cons',
    description: 'A balanced Beds24 review covering strengths, weaknesses, ease of use, support, pricing and the Malaysian compliance gap — to help you decide if Beds24 is right for your property.',
    keywords: 'beds24 review, beds24 pros and cons, beds24 opinion, is beds24 good, beds24 rating',
    updated: '2026-07-17',
    published: '2026-07-17',
    readTime: '9 min read',
    sections: [
      {
        heading: 'Summary verdict',
        body: `Beds24 is one of the most **flexible and best-value** channel manager + PMS platforms available, especially for independent operators who want control and a powerful API. Its trade-off is a **steeper learning curve** and a denser interface than polished, higher-priced rivals.

::info Bottom line: choose Beds24 if you value flexibility and price and will invest time to learn it (or work with a partner). Choose a simpler tool if you want maximum hand-holding and don't need deep customisation.`,
      },
      {
        heading: 'What Beds24 does well',
        body: `- **Channel management** — reliable two-way sync across 100+ OTAs
- **Flexibility** — almost every rate, rule, email and report is customisable
- **API & webhooks** — genuinely powerful, enabling a rich integration ecosystem
- **Pricing** — fair and scalable for independents (see [Pricing Guide](/beds24/beds24-pricing-guide))
- **Complex inventory** — dorms, multi-unit apartments and shared rooms handled well
- **Commission-free booking engine** — direct-revenue growth built in`,
      },
      {
        heading: 'Where Beds24 frustrates people',
        body: `- **Learning curve** — the interface is dense; onboarding rewards patience
- **UI polish** — functional over beautiful compared with newer rivals
- **Self-service support model** — extensive docs, but less premium hand-holding
- **Setup complexity** — powerful configuration means more decisions up front

Most complaints trace back to the same root: Beds24 gives you *lots* of control, and control has a cost in complexity.`,
      },
      {
        heading: 'Ease of use',
        body: `Honestly assessed: Beds24 is **not** the easiest tool to pick up, but it is far from impossible. New users do best by following a structured path — the [Beds24 Complete Guide](/beds24/beds24-complete-guide) — rather than clicking around. Once configured, day-to-day operation is smooth and highly automatable.`,
      },
      {
        heading: 'Support & community',
        body: `Beds24 leans on **comprehensive documentation** and an active user community, plus ticket support. If you prefer phone-first, white-glove onboarding, that expectation should be calibrated. Integration partners (for payments, analytics, and [e-Invoice](/beds24/staymii-beds24-integration)) fill much of the gap for specialised needs.`,
      },
      {
        heading: 'The Malaysian compliance caveat',
        body: `::warn For Malaysia specifically, Beds24 on its own is **not compliant** with the LHDN e-Invoice mandate — it can't submit to MyInvois.

This isn't a flaw so much as a scope boundary: Beds24 is global, and country tax rules aren't its job. The practical answer is to add [StayMii](/beds24/staymii-beds24-integration), which turns Beds24 bookings into compliant, auto-submitted e-invoices. See [Beds24 Malaysia](/beds24/beds24-malaysia).`,
      },
      {
        heading: 'Who should use Beds24 — and who should not',
        body: `**Great fit:** independent hotels, hostels, serviced apartments, homestays and short-term rentals that want flexibility, a strong API and fair pricing.

**Reconsider if:** you have a very small team, no appetite for a learning curve, and only one or two channels — a simpler tool might be less overhead.

For decision help, compare options in the Comparisons section, and read [Why Hotels Use Beds24](/beds24/why-hotels-use-beds24).`,
      },
    ],
    faqs: [
      { q: 'Is Beds24 good?', a: 'Yes — particularly for independents who value flexibility, a powerful API and fair pricing. The main downside is the learning curve.' },
      { q: 'Is Beds24 hard to use?', a: 'It has a steeper learning curve than simpler tools because it is highly configurable. Following a structured setup guide makes it manageable.' },
      { q: 'What are the main cons of Beds24?', a: 'A dense interface, a learning curve, a self-service-leaning support model, and no native Malaysian e-Invoice (solved by adding StayMii).' },
      { q: 'Does Beds24 have good support?', a: 'It offers extensive documentation, a user community and ticket support. Operators wanting premium hand-holding often lean on partners for specialised needs.' },
    ],
    related: ['what-is-beds24', 'why-hotels-use-beds24', 'beds24-pricing-guide', 'beds24-features-explained', 'staymii-beds24-integration'],
  },

  /* ============================ 8 ============================ */
  {
    slug: 'beds24-faq',
    category: 'Core',
    seoTitle: 'Beds24 FAQ: Your Top Questions Answered (2026)',
    title: 'Beds24 FAQ: The Questions Operators Ask Most',
    description: 'Clear answers to the most common Beds24 questions — what it is, pricing, channels, the API, mobile access, migration, and Malaysian e-Invoice compliance.',
    keywords: 'beds24 faq, beds24 questions, beds24 help, beds24 support, beds24 answers',
    updated: '2026-07-17',
    published: '2026-07-17',
    readTime: '8 min read',
    sections: [
      {
        heading: 'About this FAQ',
        body: `This page collects the questions we hear most from operators evaluating or running Beds24. For a full introduction, start with [What is Beds24?](/beds24/what-is-beds24); for hands-on setup, use the [Complete Guide](/beds24/beds24-complete-guide).`,
      },
      {
        heading: 'The basics',
        body: `**What is Beds24?** A cloud-based PMS + channel manager + booking engine for accommodation providers.

**Who is it for?** Independent hotels, hostels, serviced apartments, homestays and short-term rentals.

**What problem does it solve?** It prevents double bookings across channels, grows commission-free direct revenue, and automates operations.`,
      },
      {
        heading: 'Channels & bookings',
        body: `**Which channels does Beds24 support?** 100+, including Booking.com, Airbnb, Agoda, Expedia and Vrbo.

**Does it prevent overbooking?** Yes — availability closes across all channels within minutes of a sale.

**Can I take direct bookings?** Yes, via the built-in commission-free booking engine on your own website.`,
      },
      {
        heading: 'Pricing & trial',
        body: `**How much does it cost?** It's a subscription driven mainly by units/properties; it's positioned as affordable for independents. See the [Pricing Guide](/beds24/beds24-pricing-guide).

**Is there a free trial?** Yes, typically — use it to build and test your setup before paying.

**Does Beds24 charge commission?** No; it's subscription-based. OTAs still charge their own commissions.`,
      },
      {
        heading: 'Technical',
        body: `**Does Beds24 have an API?** Yes — a full REST API (v1 and v2) plus webhooks, which powers a large integration ecosystem.

**Is there a mobile app?** Beds24 is web-based and mobile-responsive; you can manage bookings from a phone browser.

**Can I migrate from another PMS?** Yes — via channel imports, calendar sync and the API. Plan room mapping carefully.`,
      },
      {
        heading: 'Malaysia & e-Invoice',
        body: `**Can Beds24 issue LHDN e-invoices?** No, not natively — it can't submit to MyInvois.

**How do I make my Beds24 setup compliant in Malaysia?** Add [StayMii](/beds24/staymii-beds24-integration), which reads Beds24 bookings and produces compliant, auto-submitted e-invoices with correct SST and tourism tax.

**Do small homestays and Airbnb hosts need e-invoices?** Yes — since July 2025 all businesses must, regardless of revenue. See [Beds24 Malaysia](/beds24/beds24-malaysia).`,
      },
      {
        heading: 'Still have questions?',
        body: `Browse the full [Beds24 Knowledge Centre](/beds24) or, if you operate in Malaysia and want to see compliant invoicing in action, [book a StayMii demo](/contact).`,
      },
    ],
    faqs: [
      { q: 'What is Beds24 used for?', a: 'Managing bookings across many channels, taking commission-free direct bookings, automating guest communication, and running day-to-day accommodation operations from one dashboard.' },
      { q: 'Does Beds24 have an app?', a: 'Beds24 is web-based and mobile-responsive, so you can manage it from a phone browser without a dedicated native app.' },
      { q: 'Can I migrate to Beds24 from another system?', a: 'Yes, using channel imports, calendar sync and the API. Careful room mapping is the key to a clean migration.' },
      { q: 'How do I make Beds24 LHDN-compliant in Malaysia?', a: 'Add StayMii to automatically turn Beds24 bookings into compliant e-invoices submitted to MyInvois, with correct SST and tourism tax.' },
    ],
    related: ['what-is-beds24', 'beds24-complete-guide', 'beds24-pricing-guide', 'beds24-malaysia', 'staymii-beds24-integration'],
  },

  /* ============================ 9 ============================ */
  {
    slug: 'staymii-beds24-integration',
    category: 'Core',
    seoTitle: 'StayMii + Beds24 Integration: Automate LHDN e-Invoice',
    title: 'StayMii + Beds24 Integration: Compliant e-Invoicing on Autopilot',
    description: 'How the StayMii Beds24 integration works — reading every Beds24 booking and turning it into an LHDN-compliant e-invoice submitted to MyInvois, with correct SST, service charge and tourism tax.',
    keywords: 'staymii beds24, beds24 e-invoice integration, beds24 myinvois, beds24 lhdn integration, beds24 staymii setup',
    updated: '2026-07-17',
    published: '2026-07-17',
    readTime: '10 min read',
    sections: [
      {
        heading: 'The problem this integration solves',
        body: `Beds24 is superb at managing bookings and channels — but it cannot issue **LHDN-compliant e-invoices**. Since Malaysia's July 2025 mandate, that's a legal requirement for every operator.

StayMii bridges the gap. It connects to your Beds24 account, reads every booking (from every channel), and turns each one into a **validated e-invoice submitted to MyInvois** — automatically. You keep Beds24 for operations; StayMii handles compliance.`,
      },
      {
        heading: 'What the integration does',
        body: `- **Real-time booking sync** from Beds24 (Booking.com, Agoda, Expedia, Airbnb, direct — all channels)
- **Correct tax calculation** per booking: **SST (8%)**, **service charge**, and **tourism tax (RM10/room/night for foreign guests)**
- **LHDN-compliant e-invoice generation** with all mandatory fields
- **Automatic submission to MyInvois**, storing the returned **UUID and QR**
- **Credit & Debit Notes** auto-generated for cancellations and rate changes
- **Reporting** for SST, tourism tax and submission status
- **Historical backfill** to cover the mandate period`,
      },
      {
        heading: 'How it connects to Beds24',
        body: `StayMii uses the **Beds24 API v2** with a secure access token you generate in your Beds24 account. The connection is read-focused: StayMii pulls booking and guest data needed to build invoices.

\`\`\`text
Beds24 (bookings)  ──API v2──▶  StayMii  ──MyInvois API──▶  LHDN
        ▲                          │
        └────  UUID + QR stored ◀──┘
\`\`\`

No migration is required and your Beds24 workflow is unchanged. See the API & Developer Guides section for token details.`,
      },
      {
        heading: 'Setup in under 15 minutes',
        body: `1. Create your **StayMii** account and complete your business profile (TIN, SSM, SST).
2. In **Beds24**, generate an **API v2 access token** and paste it into StayMii.
3. StayMii imports your **properties and rooms**; confirm the mapping.
4. Set your **tax rules** — SST rate, service charge %, tourism tax applicability.
5. Connect your **MyInvois** credentials (Client ID & Secret).
6. Run a **sandbox test**, verify the invoice, then switch on **auto-submission**.

::tip Start in the MyInvois sandbox to validate tax calculations and guest-data mapping before going live in production.`,
      },
      {
        heading: 'Handling OTA-specific invoicing',
        body: `StayMii applies the correct invoicing rule based on each booking's channel:

- **Pay-at-hotel (Booking.com, Agoda hotel-collect):** hotel issues the guest e-invoice
- **Prepaid (Agoda/Expedia OTA-collect):** hotel still issues the guest e-invoice; commission recorded as expense
- **Airbnb:** host invoices the guest; Airbnb host fee is a separate expense
- **Missing guest TIN:** LHDN general public TIN used where the guest has none

This removes the most error-prone part of Malaysian OTA invoicing. Background in [Beds24 Malaysia](/beds24/beds24-malaysia).`,
      },
      {
        heading: 'Why not build it yourself?',
        body: `A direct MyInvois integration means OAuth token management, precise tax calculations, strict document formatting, credit/debit note logic, error handling, and ongoing maintenance as LHDN evolves the API. StayMii maintains all of this for you and provides a dashboard to monitor submissions and fix rejections — so your team never touches the API.`,
      },
      {
        heading: 'Get started',
        body: `If you run Beds24 in Malaysia, this is the fastest route to full e-Invoice compliance without leaving your existing system.

[Book a StayMii demo](/contact) to see it with your own Beds24 data, or explore the [Beds24 e-Invoice integration page](/integrations/beds24-e-invoice) for more detail.`,
      },
    ],
    faqs: [
      { q: 'Does StayMii replace Beds24?', a: 'No. StayMii works alongside Beds24. You keep Beds24 for bookings and channels; StayMii adds LHDN e-Invoice automation on top.' },
      { q: 'What do I need from Beds24 to connect?', a: 'A Beds24 API v2 access token, which you generate in your Beds24 account and paste into StayMii. No migration is needed.' },
      { q: 'How long does setup take?', a: 'Typically under 15 minutes: connect Beds24, map rooms, set tax rules, connect MyInvois, test in sandbox, then enable auto-submission.' },
      { q: 'Does it handle cancellations and refunds?', a: 'Yes. StayMii automatically generates Credit and Debit Notes for cancellations and rate changes, referencing the original invoice.' },
    ],
    related: ['beds24-malaysia', 'what-is-beds24', 'beds24-complete-guide', 'beds24-features-explained', 'beds24-resource-centre'],
  },

  /* ============================ 10 ============================ */
  {
    slug: 'beds24-resource-centre',
    category: 'Core',
    seoTitle: 'Beds24 Resource Centre: All Guides in One Place',
    title: 'The Beds24 Resource Centre: Your Map to Every Guide',
    description: 'A curated index of the StayMii Beds24 Knowledge Centre — foundation guides, API documentation, Malaysian e-Invoice, tutorials and comparisons, organised so you can find exactly what you need.',
    keywords: 'beds24 resources, beds24 knowledge base, beds24 documentation, beds24 help centre, beds24 guides',
    updated: '2026-07-17',
    published: '2026-07-17',
    readTime: '7 min read',
    sections: [
      {
        heading: 'How to use this resource centre',
        body: `The StayMii Beds24 Knowledge Centre is organised into five sections: **Foundation**, **API & Developer Guides**, **Malaysian e-Invoice**, **Tutorials**, and **Comparisons**. This page is your map — pick the section that matches where you are in your Beds24 journey. You can also browse everything from the [Beds24 hub](/beds24).`,
      },
      {
        heading: 'Start here: Foundation',
        body: `New to Beds24? Read these first, in order:

- [What is Beds24?](/beds24/what-is-beds24) — the concepts in plain English
- [Beds24 Complete Guide](/beds24/beds24-complete-guide) — full setup, step by step
- [Beds24 Features Explained](/beds24/beds24-features-explained) — every feature, decoded
- [Why Hotels Use Beds24](/beds24/why-hotels-use-beds24) — the real reasons operators choose it
- [Beds24 Review](/beds24/beds24-review) — honest pros and cons
- [Beds24 Pricing Guide](/beds24/beds24-pricing-guide) — how the cost works
- [Beds24 FAQ](/beds24/beds24-faq) — quick answers to common questions`,
      },
      {
        heading: 'For Malaysia: compliance & e-Invoice',
        body: `Operating in Malaysia? These are essential:

- [Beds24 Malaysia](/beds24/beds24-malaysia) — local setup, SST, tourism tax, and the e-Invoice mandate
- [StayMii + Beds24 Integration](/beds24/staymii-beds24-integration) — automate compliant e-invoices to MyInvois

::warn Since July 2025, every Malaysian operator must issue LHDN e-invoices. Beds24 can't do this alone — pairing it with StayMii makes your setup compliant.`,
      },
      {
        heading: 'For developers: API & webhooks',
        body: `The **API & Developer Guides** section covers authentication, endpoints and webhooks for building on Beds24 — including how StayMii uses the Beds24 API v2 to power e-Invoice automation. These guides are published progressively; check the [hub](/beds24) for the latest.`,
      },
      {
        heading: 'Hands-on: tutorials',
        body: `The **Tutorials** section provides step-by-step walkthroughs — connecting channels like Booking.com, Agoda and Airbnb, wiring up Stripe payments, configuring webhooks, and troubleshooting sync issues. If you learn by doing, start there after the [Complete Guide](/beds24/beds24-complete-guide).`,
      },
      {
        heading: 'Deciding: comparisons',
        body: `Still choosing a platform, or considering a switch? The **Comparisons** section puts Beds24 head-to-head with Cloudbeds, Little Hotelier, Hotelogix and eZee, and rounds up the best PMS and channel-manager options for Malaysia. Combine these with the [Review](/beds24/beds24-review) and [Pricing Guide](/beds24/beds24-pricing-guide) to decide with confidence.`,
      },
      {
        heading: 'Get help with your specific setup',
        body: `If you'd rather talk it through with someone who knows both Beds24 and Malaysian compliance, [book a StayMii demo](/contact). We'll look at your Beds24 account and show you exactly how compliant, automated invoicing would work for your property.`,
      },
    ],
    faqs: [
      { q: 'Where should I start if I am new to Beds24?', a: 'Read What is Beds24?, then the Beds24 Complete Guide. Malaysian operators should also read Beds24 Malaysia and the StayMii integration guide.' },
      { q: 'Is the Knowledge Centre free?', a: 'Yes. All guides are free to read. StayMii is the paid product that adds LHDN e-Invoice automation on top of Beds24.' },
      { q: 'How often is the content updated?', a: 'Regularly. Each article shows a "last updated" date, and new API, tutorial and comparison guides are published on a rolling schedule.' },
      { q: 'Do you cover other PMS platforms?', a: 'This centre focuses on Beds24, but the Comparisons section covers alternatives like Cloudbeds, Little Hotelier and eZee.' },
    ],
    related: ['what-is-beds24', 'beds24-complete-guide', 'beds24-malaysia', 'staymii-beds24-integration', 'beds24-faq'],
  },
];

export const findBeds24Article = (pathname) => {
  const slug = (pathname || '').replace(/^\/beds24\//, '').replace(/\/$/, '');
  return beds24Articles.find(a => a.slug === slug) || null;
};

export default beds24Articles;
